import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { favoriteService, hiddenService } from '../api';
import AuthModal from './auth/AuthModal';
import { trackSelectItem, trackAddToWishlist } from '../utils/analytics';
import './AdCard.css';

export const KIND_LABELS = {
    Apartment:  'Квартира',
    Room:       'Комната',
    House:      'Дом',
    HousePart:  'Часть дома',
    Townhouse:  'Таунхаус',
    Bed:        'Койко-место',
    Garage:     'Гараж',
    LandPlot:   'Участок',
    Office:     'Офис',
    Retail:     'Торговая площадь',
    Warehouse:  'Склад',
    Coworking:  'Коворкинг',
    Business:   'Бизнес',
};

const SELLER_LABELS = {
    0: 'Собственник',
    1: 'Агент',
    2: 'Застройщик',
};

export function formatPrice(price) {
    if (price == null) return '—';
    return price.toLocaleString('ru-RU') + ' ₽';
}

export function buildSpecs(ad) {
    const parts = [];
    if (ad.rooms)          parts.push(`${ad.rooms}-комн.`);
    if (ad.area)           parts.push(`${ad.area} м²`);
    if (ad.floor != null && ad.buildingFloors != null)
        parts.push(`${ad.floor}/${ad.buildingFloors} эт.`);
    else if (ad.floor != null)
        parts.push(`${ad.floor} эт.`);
    else if (ad.buildingFloors != null)
        parts.push(`${ad.buildingFloors} эт.`);
    return parts.join(' · ');
}

function CarouselPhoto({ photos, alt }) {
    const [index, setIndex] = useState(0);
    const containerRef = useRef(null);
    // Touch swipe state; only engages when the gesture starts on the photo itself,
    // so swiping elsewhere on the card (e.g. inside a horizontal strip) scrolls normally.
    const touchRef = useRef({ x: 0, y: 0, swiping: false });

    const handleMouseMove = useCallback((e) => {
        if (!photos || photos.length <= 1) return;
        const rect = containerRef.current.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        const newIndex = Math.min(
            photos.length - 1,
            Math.floor(ratio * photos.length)
        );
        setIndex(newIndex);
    }, [photos]);

    const handleMouseLeave = useCallback(() => setIndex(0), []);

    const handleTouchStart = useCallback((e) => {
        if (!photos || photos.length <= 1) return;
        const t = e.touches[0];
        touchRef.current = { x: t.clientX, y: t.clientY, swiping: false };
    }, [photos]);

    const handleTouchMove = useCallback((e) => {
        if (!photos || photos.length <= 1) return;
        const t = e.touches[0];
        const dx = t.clientX - touchRef.current.x;
        const dy = t.clientY - touchRef.current.y;
        if (!touchRef.current.swiping && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
            touchRef.current.swiping = true;
        }
    }, [photos]);

    const handleTouchEnd = useCallback((e) => {
        if (!touchRef.current.swiping) return;
        const dx = e.changedTouches[0].clientX - touchRef.current.x;
        const THRESHOLD = 30;
        if (dx <= -THRESHOLD) setIndex(i => Math.min(photos.length - 1, i + 1));
        else if (dx >= THRESHOLD) setIndex(i => Math.max(0, i - 1));
    }, [photos]);

    // Block the card's navigation click if the tap was actually a swipe
    const handleClickCapture = useCallback((e) => {
        if (touchRef.current.swiping) {
            e.preventDefault();
            e.stopPropagation();
            touchRef.current.swiping = false;
        }
    }, []);

    const src = photos?.[index];

    return (
        <div
            className="listing-card__photo-wrap"
            ref={containerRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onClickCapture={handleClickCapture}
        >
            {src
                ? <img className="listing-card__photo" src={src} alt={alt} />
                : <div className="listing-card__photo--placeholder">Нет фото</div>
            }
            {photos && photos.length > 1 && (
                <div className="listing-card__dots">
                    {photos.map((_, i) => (
                        <span
                            key={i}
                            className={`listing-card__dot${i === index ? ' listing-card__dot--active' : ''}`}
                        />
                    ))}
                </div>
            )}
            {photos && photos.length > 0 && (
                <span className="listing-card__photo-count">{photos.length}</span>
            )}
        </div>
    );
}

function FavoriteBtn({ adId, ad, initialActive, onRequireAuth, onChange }) {
    const { user } = useAuth();
    const [active, setActive] = useState(!!initialActive);
    const [busy, setBusy] = useState(false);

    useEffect(() => { setActive(!!initialActive); }, [initialActive]);

    const handleClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!user) { onRequireAuth(); return; }
        if (busy) return;

        const next = !active;
        setActive(next);
        setBusy(true);
        try {
            if (next) { await favoriteService.addFavorite(adId); trackAddToWishlist(ad); }
            else await favoriteService.removeFavorite(adId);
            onChange?.(adId, next);
        } catch {
            setActive(!next);
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            className={`listing-card__fav${active ? ' listing-card__fav--active' : ''}`}
            onClick={handleClick}
            title="В избранное"
        >
            <svg width="20" height="20" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
        </button>
    );
}

function HideMenuBtn({ ad, onRequireAuth, onHidden }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    if (user && ad.userId && ad.userId === user.id) return null;

    const toggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!user) { onRequireAuth(); return; }
        setOpen(o => !o);
    };

    const hideAd = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        try {
            await hiddenService.hideAd(ad.id);
            onHidden({ adId: ad.id });
        } catch { /* ignore */ }
    };

    const hideSeller = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        if (!ad.userId) return;
        try {
            await hiddenService.hideSeller(ad.userId);
            onHidden({ sellerId: ad.userId });
        } catch { /* ignore */ }
    };

    return (
        <div className="listing-card__hide-menu" ref={wrapRef}>
            <button className="listing-card__hide-trigger" onClick={toggle} title="Скрыть">⋮</button>
            {open && (
                <div className="listing-card__hide-dropdown">
                    <button onClick={hideAd}>Скрыть это объявление</button>
                    {ad.userId && <button onClick={hideSeller}>Скрыть все объявления автора</button>}
                </div>
            )}
        </div>
    );
}

function RestoreBtn({ adId, onRestore }) {
    const [busy, setBusy] = useState(false);

    const handleClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        try {
            await hiddenService.unhideAd(adId);
            onRestore?.(adId);
        } finally {
            setBusy(false);
        }
    };

    return (
        <button className="listing-card__fav" onClick={handleClick} title="Показать" disabled={busy}>
            Показать
        </button>
    );
}

export default function AdCard({ ad, view = 'grid', onHidden, onFavoriteChange, restoreMode = false, onRestore, listName = 'catalog' }) {
    const specs = buildSpecs(ad);
    const kindLabel = KIND_LABELS[ad.propertyKind] ?? ad.propertyKind;
    const sellerLabel = ad.sellerType != null ? SELLER_LABELS[ad.sellerType] : null;
    const address = ad.addressDisplay || ad.city || null;
    const [authOpen, setAuthOpen] = useState(false);
    const requireAuth = () => setAuthOpen(true);
    const handleHidden = (payload) => onHidden?.(ad, payload);
    const handleSelect = () => trackSelectItem(ad, listName);

    if (view === 'list') {
        return (
            <>
            <Link to={`/ad/${ad.id}`} className="listing-card listing-card--list" onClick={handleSelect}>
                <CarouselPhoto photos={ad.photoUrls} alt={ad.title} />
                <div className="listing-card__body">
                    <div className="listing-card__header">
                        <div className="listing-card__title-wrap">
                            <span className="listing-card__title">{ad.title}</span>
                        </div>
                        <div className="listing-card__header-actions">
                            {restoreMode ? (
                                <RestoreBtn adId={ad.id} onRestore={onRestore} />
                            ) : (
                                <>
                                    <FavoriteBtn adId={ad.id} ad={ad} initialActive={ad.isFavorite} onRequireAuth={requireAuth} onChange={onFavoriteChange} />
                                    <HideMenuBtn ad={ad} onRequireAuth={requireAuth} onHidden={handleHidden} />
                                </>
                            )}
                        </div>
                    </div>
                    <span className="listing-card__price">{formatPrice(ad.price)}</span>
                    {specs && <span className="listing-card__specs">{specs}</span>}
                    {address && <span className="listing-card__address">{address}</span>}
                    {ad.description && (
                        <p className="listing-card__description">{ad.description}</p>
                    )}
                    {(ad.authorName || sellerLabel) && (
                        <span className="listing-card__author">
                            {[ad.authorName, sellerLabel].filter(Boolean).join(' · ')}
                        </span>
                    )}
                </div>
            </Link>
            <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
            </>
        );
    }

    return (
        <>
        <Link to={`/ad/${ad.id}`} className="listing-card listing-card--grid" onClick={handleSelect}>
            <div className="listing-card__photo-container">
                <CarouselPhoto photos={ad.photoUrls} alt={ad.title} />
                <div className="listing-card__grid-actions">
                    {restoreMode ? (
                        <RestoreBtn adId={ad.id} onRestore={onRestore} />
                    ) : (
                        <>
                            <FavoriteBtn adId={ad.id} ad={ad} initialActive={ad.isFavorite} onRequireAuth={requireAuth} onChange={onFavoriteChange} />
                            <HideMenuBtn ad={ad} onRequireAuth={requireAuth} onHidden={handleHidden} />
                        </>
                    )}
                </div>
            </div>
            <div className="listing-card__body">
                <span className="listing-card__title">{ad.title}</span>
                <span className="listing-card__price">{formatPrice(ad.price)}</span>
                {specs && <span className="listing-card__specs">{specs}</span>}
                {address && <span className="listing-card__address">{address}</span>}
            </div>
        </Link>
        <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
        </>
    );
}
